/**
 * The locale cookie, `mauvely_locale`.
 *
 * Written when somebody chooses a locale — from the switcher or the banner —
 * and read by the Worker when a bare path arrives, so the choice outlives the
 * tab. Scoped to `.mauvely.com` for the same reason `mauvely_privacy` is: the
 * other sites will read it when they gain locales of their own.
 *
 * The URL is the source of truth while a page is open; this is only what the
 * front door falls back to. Nothing in the app reads it back.
 */

import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  LOCALE_COOKIE_MAX_AGE,
  cookieDomainAttr,
  isLocale,
  splitLocale
} from '@shared/locales';

/** The locale in the address bar right now, or the default when the app was
 *  reached on a bare path (the Vite dev server, which bypasses the Worker).
 *  Read at call time, never closed over — see router.tsx for why. */
export function currentLocale(): string {
  return splitLocale(window.location.pathname).locale ?? DEFAULT_LOCALE;
}

export function writeLocaleCookie(tag: string): void {
  if (!isLocale(tag)) return;
  try {
    document.cookie =
      `${LOCALE_COOKIE}=${tag}; Path=/${cookieDomainAttr(window.location.hostname)}` +
      `; Max-Age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax` +
      (window.location.protocol === 'https:' ? '; Secure' : '');
  } catch {
    // Cookies disabled: the front door detects afresh next time, which is
    // the right behaviour rather than a bug.
  }
}

/** Whether a choice has been recorded. Last cookie of the name wins, as in
 *  lib/consent.ts. */
export function hasLocaleCookie(): boolean {
  try {
    let found = false;
    for (const part of document.cookie.split(';')) {
      const [name, ...rest] = part.trim().split('=');
      if (name === LOCALE_COOKIE && isLocale(rest.join('='))) found = true;
    }
    return found;
  } catch {
    return false;
  }
}
